import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Radio from "antd/es/radio";
import { useIntl } from "react-intl";

import {
  Company,
  Deal,
  EntitiesSupportingGroups,
  EntityType,
  EntityTypeSupportingGroups,
  Group,
  Person,
} from "@mapmycustomers/shared/types/entity";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Labeled, SelectField } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import { CreateGroupModal } from "@app/component/createEditEntity/Group";
import useGroupTagRender from "@app/component/EntityTag/useGroupTagRender";
import RadioGroup from "@app/component/input/RadioGroup";
import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import { isUpdating } from "@app/component/view/SelectBar/store";
import {
  bulkAddEntitiesToGroups,
  bulkRemoveEntitiesFromGroups,
} from "@app/component/view/SelectBar/store/actions";
import BulkGroupOperation, { getBulkGroupOperationDisplayName } from "@app/enum/BulkGroupOperation";
import { getGroupsForEntity } from "@app/store/groups";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";

import styles from "../../CommonModals.module.scss";
import BaseModal from "../BaseModal";
import EntityTypeSelector from "../EntityTypeSelector";

import messages from "./utils/messages";
import useSubmitHandler from "./utils/useSubmitHandler";

interface Props {
  entities: Array<EntitiesSupportingGroups>;
  onHide: () => void;
  onReloadData?: () => void;
}

type ComponentProps = Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

type GroupId = Group["id"];
type GroupIdArray = Array<GroupId>;

const GroupsModal: React.FC<ComponentProps> = ({
  bulkAddEntitiesToGroups,
  bulkRemoveEntitiesFromGroups,
  companyGroups,
  dealGroups,
  entities,
  onHide,
  onReloadData,
  peopleGroups,
  updating,
}) => {
  const intl = useIntl();
  const analyticsIssuer = useAnalytics();
  useEffect(() => {
    analyticsIssuer.using(["Add/Remove from Groups"]);
  }, [analyticsIssuer]);

  const [groupIdsToAdd, setGroupIdsToAdd] = useState<GroupIdArray>([]);
  const [groupIdsToRemove, setGroupIdsToRemove] = useState<GroupIdArray>([]);
  const [operation, setOperation] = useState<BulkGroupOperation>(BulkGroupOperation.ADD);
  const entityTypes = useMemo(
    () => Array.from(new Set(entities.map(({ entity }) => entity))),
    [entities]
  );

  const [entityType, setEntityType] = useState<EntityTypeSupportingGroups>(entityTypes[0]);

  const groups = useMemo(
    () =>
      (entityType === EntityType.COMPANY
        ? companyGroups
        : entityType === EntityType.PERSON
        ? peopleGroups
        : dealGroups
      ).filter(({ accessStatus }) => accessStatus.update),
    [companyGroups, dealGroups, entityType, peopleGroups]
  );

  useEffect(() => {
    setGroupIdsToAdd([]);
    setGroupIdsToRemove([]);
  }, [entityType]);

  const tagRenderer = useGroupTagRender(groups);

  const groupOptions = useMemo(() => {
    let result = [...groups];
    if (operation === BulkGroupOperation.REMOVE) {
      const allowedGroupIds = new Set(
        (entities as (Company | Deal | Person)[]).reduce<Group["id"][]>(
          (result, entity) => [...result, ...(entity.groups?.map(({ id }) => id) ?? [])],
          []
        )
      );
      result = result.filter(({ id }) => allowedGroupIds.has(id));
    }
    return result.sort(nameComparator).map(defaultSelectOptionMapper);
  }, [entities, groups, operation]);

  const handleChange = useCallback(
    (values: GroupIdArray) => {
      if (operation === BulkGroupOperation.ADD) {
        setGroupIdsToAdd(values);
      } else {
        setGroupIdsToRemove(values);
      }
    },
    [operation, setGroupIdsToAdd, setGroupIdsToRemove]
  );

  const hasOptions = groups && groupOptions.length > 0;
  const isAdding = operation === BulkGroupOperation.ADD;
  const groupIds = isAdding ? groupIdsToAdd : groupIdsToRemove;
  const hasValue = !!groupIds && groupIds.length > 0;
  const isApplyDisabled = !hasOptions || !hasValue || updating;

  const [newGroupModalVisible, showNewGroupModal, hideNewGroupModal] = useBoolean();

  const handleHideCreateGroupModel = useCallback(() => hideNewGroupModal(), [hideNewGroupModal]);
  const handleCreateGroup = useCallback(
    (group: Group) => setGroupIdsToAdd((selectedIds) => [...selectedIds, group.id]),
    [setGroupIdsToAdd]
  );

  const handleApply = useSubmitHandler(
    bulkAddEntitiesToGroups,
    bulkRemoveEntitiesFromGroups,
    operation,
    // type cast safety is guaranteed in AddRemoveButton where each operation has a list of supported entity types
    entityType as EntityTypeSupportingGroups,
    entities as EntitiesSupportingGroups[],
    groupIds,
    useCallback(() => {
      analyticsIssuer.using(["Add/Remove from Groups", "Apply"]);
      onHide();
      onReloadData?.();
    }, [analyticsIssuer, onHide, onReloadData])
  );

  return (
    <BaseModal
      applyDisabled={isApplyDisabled}
      applyLoading={updating}
      onApply={handleApply}
      onHide={onHide}
      title={intl.formatMessage(messages.title)}
    >
      <div className={styles.description}>{intl.formatMessage(messages.description)}</div>

      {entityTypes.length > 1 && (
        <EntityTypeSelector
          entityTypes={entityTypes}
          onChange={(type) => setEntityType(type as EntityTypeSupportingGroups)}
          value={entityType}
        />
      )}

      <Labeled label={intl.formatMessage(messages.selectOne)}>
        <RadioGroup className={styles.operation} onChange={setOperation} value={operation}>
          <Radio.Button value={BulkGroupOperation.ADD}>
            {getBulkGroupOperationDisplayName(intl, BulkGroupOperation.ADD)}
          </Radio.Button>

          <Radio.Button disabled={!hasOptions} value={BulkGroupOperation.REMOVE}>
            {getBulkGroupOperationDisplayName(intl, BulkGroupOperation.REMOVE)}
          </Radio.Button>
        </RadioGroup>
      </Labeled>

      <Labeled label={intl.formatMessage(messages.selectGroup)}>
        <SelectField<GroupIdArray>
          className={styles.select}
          disabled={!hasOptions || updating}
          filterOption={defaultFilterOption}
          loading={updating}
          mode="multiple"
          onChange={handleChange}
          options={groupOptions}
          placeholder={intl.formatMessage(messages.groupsPlaceholder)}
          showArrow
          showSearch
          tagRender={tagRenderer}
          value={groupIds}
        />

        {isAdding ? (
          <ButtonLink className={styles.addButton} onClick={showNewGroupModal}>
            {intl.formatMessage(messages.create)}
          </ButtonLink>
        ) : null}
      </Labeled>
      <CreateGroupModal
        entityType={entityType as EntityTypeSupportingGroups}
        onCreate={handleCreateGroup}
        onHide={handleHideCreateGroupModel}
        visible={newGroupModalVisible}
      />
    </BaseModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  companyGroups: getGroupsForEntity(state)(EntityType.COMPANY),
  dealGroups: getGroupsForEntity(state)(EntityType.DEAL),
  peopleGroups: getGroupsForEntity(state)(EntityType.PERSON),
  updating: isUpdating(state),
});

const mapDispatchToProps = {
  bulkAddEntitiesToGroups: bulkAddEntitiesToGroups.request,
  bulkRemoveEntitiesFromGroups: bulkRemoveEntitiesFromGroups.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupsModal);
